import { useEffect } from "react";
import Lenis from "lenis";

let _lenis = null;
export const getLenis = () => _lenis;

/** Fixed navbar clearance for section anchors */
export const NAV_SCROLL_OFFSET = -80;

const DESIRED_TOP = -NAV_SCROLL_OFFSET; // ~80px below viewport top

/**
 * Expand layout-shifting UI (process accordion) before measuring scroll targets.
 */
function prepareLayoutForScroll() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("przestrzen:prepare-scroll"));
}

function resolveTarget(target) {
  if (typeof target !== "string") return target;
  const id = target.replace(/^#/, "");
  if (!id || id === "top") return 0;
  return document.getElementById(id);
}

/**
 * Scroll to a section id / hash / element / number via Lenis when available.
 * Falls back to window.scrollTo so it still works off the home page.
 */
export function scrollToSection(target, options = {}) {
  const {
    offset = NAV_SCROLL_OFFSET,
    duration = 1.35,
    immediate = false,
    lock = true,
    onComplete,
  } = options;

  prepareLayoutForScroll();

  const run = (pass = 0) => {
    const resolved = resolveTarget(target);
    if (resolved === null || resolved === false) return false;

    const lenis = getLenis();
    const isTop = resolved === 0;
    const el = resolved instanceof HTMLElement ? resolved : null;

    if (!isTop && !el) {
      // Element not in DOM yet
      if (pass < 8) {
        requestAnimationFrame(() => run(pass + 1));
        return true;
      }
      return false;
    }

    const done = () => {
      // Correct residual error from late layout shifts (images, accordion, fonts)
      if (el && pass < 2) {
        const err = Math.abs(el.getBoundingClientRect().top - DESIRED_TOP);
        if (err > 40) {
          requestAnimationFrame(() => run(pass + 1));
          return;
        }
      }
      onComplete?.();
    };

    if (lenis) {
      lenis.scrollTo(isTop ? 0 : el, {
        offset: isTop ? 0 : offset,
        duration: immediate ? 0 : pass === 0 ? duration : 0.55,
        immediate,
        lock,
        force: true,
        onComplete: done,
      });
      return true;
    }

    // Fallback without Lenis (blog / case study pages)
    if (isTop) {
      window.scrollTo({ top: 0, behavior: immediate ? "auto" : "smooth" });
      done();
      return true;
    }

    const top = el.getBoundingClientRect().top + window.scrollY + offset;
    window.scrollTo({ top, behavior: immediate ? "auto" : "smooth" });
    // smooth fallback: re-check after animation
    if (!immediate) setTimeout(done, 500);
    else done();
    return true;
  };

  // Two frames: React applies process expand, then layout is measurable
  requestAnimationFrame(() => {
    requestAnimationFrame(() => run(0));
  });

  return true;
}

export default function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      // Anchor clicks are handled in HashScroll (preventDefault + scrollToSection)
      anchors: false,
      stopInertiaOnNavigate: true,
    });
    _lenis = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    const rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      _lenis = null;
    };
  }, []);
}
